import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useCanvases } from '@/components/canvas/CanvasProvider';
import { CanvasNumber, ProgressBar, StatusBadge, canvasLabel, statusKey } from '@/components/canvas/bits';
import PackView, { IndexStrip } from '@/components/canvas/PackView';
import { buildPackModel, downloadPackPdf } from '@/components/canvas/packTools';

export const route = { path: '/pack', layout: 'owner', access: "profile === 'Learner_c'" };
export const nav = { icon: 'FileDown', label: 'My pack', order: 90, profiles: ['Learner_c'] };

function Readiness({ defs, states }) {
  return (
    <ul className="divide-y divide-border rounded-2xl border border-border bg-card shadow-xs">
      {defs.map((d) => {
        const st = states[d.no];
        const key = statusKey(st);
        return (
          <li key={d.no}>
            <Link
              to={`/canvas/${d.no}`}
              className="flex items-center gap-3 px-4 py-3 outline-none transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <CanvasNumber def={d} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {canvasLabel(d)}
                  {d.no !== 0 && <span className="text-muted-foreground">: {d.title}</span>}
                </p>
                {key === 'Review' && <p className="text-xs text-muted-foreground">Changed upstream. Check it and confirm again.</p>}
                {key === 'In progress' && (
                  <p className="text-xs text-muted-foreground">
                    {st.missing.length} answer{st.missing.length === 1 ? '' : 's'} still needed
                  </p>
                )}
              </div>
              <StatusBadge state={st} />
              <ApperIcon name="ChevronRight" size={16} className="text-muted-foreground" />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

export default function Pack() {
  const { loading, loadError, reload, needsWelcome, defs, states, agg, profile, packReady, flush } = useCanvases();
  const [busy, setBusy] = useState(null);

  const model = useMemo(
    () => (loading ? null : buildPackModel({ states, agg, learner: profile })),
    [loading, states, agg, profile],
  );

  if (needsWelcome) return <Navigate to="/welcome" replace />;

  if (loading || !model) {
    return (
      <div className="mx-auto max-w-4xl space-y-6" aria-busy="true">
        <Skeleton className="h-9 w-56" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-96 w-full rounded-2xl" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-xl space-y-4 rounded-2xl border border-destructive/40 bg-destructive/10 p-6">
        <h1 className="font-heading text-xl font-semibold">Your pack did not load</h1>
        <p className="text-sm">{loadError}</p>
        <Button onClick={reload}>Try again</Button>
      </div>
    );
  }

  const list = defs.map((d) => states[d.no]);
  const confirmed = list.filter((s) => s?.confirmed && !s?.stale).length;
  const remaining = defs.length - confirmed;

  async function download(onlyConfirmed) {
    setBusy(onlyConfirmed ? 'draft' : 'full');
    try {
      await flush();
      const fresh = buildPackModel({ states, agg, learner: profile });
      await downloadPackPdf(fresh, profile, { onlyConfirmed });
      toast.success(onlyConfirmed ? 'Your draft PDF is downloading.' : 'Your PDF is downloading.');
    } catch {
      toast.error('The PDF could not be made. Please try again.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <div className="space-y-1">
        <h1 className="font-heading text-3xl font-semibold text-balance">My pack</h1>
        <p className="text-muted-foreground">
          Your ten canvases as one clean A4 report, with the Evidence Gaps and Buy-in Ledger at the back. You can sign in again at any time, change an answer, and download a new copy.
        </p>
      </div>

      <section
        className={cn(
          'space-y-4 rounded-2xl border p-5 shadow-xs md:p-6',
          packReady ? 'border-success-border bg-success-muted' : 'border-border bg-card',
        )}
      >
        <div className="flex flex-wrap items-start gap-4">
          <span
            className={cn(
              'flex size-12 shrink-0 items-center justify-center rounded-2xl text-white',
              packReady ? 'bg-success' : 'bg-(color:--step-1)',
            )}
          >
            <ApperIcon name={packReady ? 'CircleCheck' : 'FileText'} size={24} />
          </span>
          <div className="min-w-0 flex-1 space-y-1">
            <h2 className="font-heading text-lg font-semibold">
              {packReady ? 'Your pack is ready' : `${remaining} ${remaining === 1 ? 'canvas' : 'canvases'} to confirm before the full pack`}
            </h2>
            <p className="text-sm text-muted-foreground">
              {packReady
                ? `Prepared for ${profile.name}${profile.organisation ? `, ${profile.organisation}` : ''}. Every canvas is confirmed.`
                : 'Confirm each canvas on its own page. A canvas you changed after confirming shows as Review until you confirm it again.'}
            </p>
            <ProgressBar value={(confirmed / defs.length) * 100} label="Canvases confirmed" className="mt-2 max-w-sm" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="lg" disabled={!packReady || busy !== null} onClick={() => download(false)}>
            {busy === 'full' ? (
              <>
                <ApperIcon name="LoaderCircle" size={18} className="animate-spin" /> Making your PDF
              </>
            ) : (
              <>
                <ApperIcon name="FileDown" size={18} /> Download PDF
              </>
            )}
          </Button>
          {!packReady && confirmed > 0 && (
            <Button size="lg" variant="outline" disabled={busy !== null} onClick={() => download(true)}>
              {busy === 'draft' ? (
                <>
                  <ApperIcon name="LoaderCircle" size={18} className="animate-spin" /> Making your PDF
                </>
              ) : (
                <>
                  <ApperIcon name="FileDown" size={18} /> Download a draft of the {confirmed} confirmed
                </>
              )}
            </Button>
          )}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="font-heading text-lg font-semibold">Where each canvas stands</h2>
        <Readiness defs={defs} states={states} />
      </section>

      <section className="space-y-3">
        <h2 className="font-heading text-lg font-semibold">What the PDF will say</h2>
        <IndexStrip model={model} />
        <PackView model={model} linkTo={(no) => `/canvas/${no}`} />
      </section>
    </div>
  );
}
